"use client";

import { Check, ChevronsUpDown } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import * as React from "react";

import { Loading } from "@/components/icons/loading";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatPageType, parseHTML } from "@/lib/utils/index";

interface EDBSelectOptionProps {
	option: any;
	value: string;
	setValue: React.Dispatch<React.SetStateAction<string>>;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	style?: React.CSSProperties;
}

const EDBSelectOption = ({ option, value, setValue, setOpen, ...props }: EDBSelectOptionProps) => {
	const router = useRouter();
	const { overview } = formatPageType(option.page_type ?? option.type);

	return (
		<CommandItem
			value={option.slug}
			onSelect={(currentValue) => {
				setValue(currentValue === value ? "" : currentValue);
				router.push(
					`/database/${currentValue}?type=${
						option.intervention_type?.toLowerCase() ?? option?.type?.toLowerCase()
					}`
				);
				setOpen(false);
			}}
			className={cn(
				value === option.slug ? "bg-gray-200 font-semibold text-primary" : "",
				"flex cursor-pointer items-start justify-between px-4 py-2.5 text-gray-900 hover:text-primary lg:hover:bg-gray-100"
			)}
			{...props}
		>
			<span className="inline-flex items-center">
				<Check
					className={cn("mr-1 h-4 w-4", value === option.slug ? "block" : "hidden")}
					strokeWidth={3}
				/>
				{option.name}
			</span>
			<Tooltip>
				<TooltipTrigger>
					<span className="view-page ml-1 inline whitespace-nowrap rounded-full border border-gray-200 bg-white px-2 py-1 font-sans text-[11px] font-medium leading-3 text-examine-green-500">
						{option.intervention_type?.toLowerCase() ?? option?.type?.toLowerCase()}
					</span>
				</TooltipTrigger>
				<TooltipContent className="[@media(max-width:768px)]:hidden">
					<p>
						{parseHTML({
							html: overview,
						})}
					</p>
				</TooltipContent>
			</Tooltip>
		</CommandItem>
	);
};

interface Option {
	name: string;
	slug: string;
	intervention_type?: string;
	type?: string;
}

interface EDBSelectProps {
	options: Option[];
	selected?: string;
	onChange?: (value: string) => void;
}

const EDBSelect = ({ options, selected, onChange }: EDBSelectProps) => {
	const pathname = usePathname();
	const [open, setOpen] = React.useState(false);
	const [value, setValue] = React.useState(pathname.split("/")[2] || "");
	const [isPending, startTransition] = React.useTransition();

	const handleOpenChange = () => {
		startTransition(() => {
			setOpen((prev) => !prev);
		});
	};

	return (
		<div className="relative flex max-w-3xl py-4">
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						className="group relative w-full justify-between border-gray-400 hover:bg-gray-100 active:border-gray-400 active:bg-gray-100 lg:px-5 lg:py-[15px]"
					>
						<span className={cn("py-1 text-black", !value ? "text-gray-600" : "")}>
							{value
								? options.find((option: Option) => option.slug === value)?.name
								: "Select a topic to get started"}
						</span>
						<span className="absolute bottom-0 right-0 top-0 -m-px grid w-14 place-content-center place-items-center rounded-r bg-primary text-white">
							{isPending && !open ? (
								<div className="grid h-60 place-content-center">
									<Loading className="text-white" />
								</div>
							) : (
								<ChevronsUpDown className="stoke-2 size-5 shrink-0 stroke-2 opacity-70 lg:size-6" />
							)}
						</span>
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[85vw] p-0 lg:w-[768px]">
					<Command>
						<CommandInput placeholder="Search..." />
						<CommandList>
							<CommandEmpty>Topic not found.</CommandEmpty>
							<CommandGroup>
								{options?.map((option: Option, i: React.Key) => (
									<EDBSelectOption
										option={option}
										key={option.slug + i}
										value={value}
										setValue={setValue}
										setOpen={setOpen}
									/>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export { EDBSelect };
